import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Heart, MessageSquare, Send, CheckCircle, Sparkles, Calendar } from 'lucide-react';
import SubmissionPopup from '../components/SubmissionPopup';
import MouseFollowEffect from '../components/MouseFollowEffect';
import QuestionNavigator from '../components/QuestionNavigator';

interface FormData {
  name: string;
  email: string;
  company: string;
  answers: Record<string, string | string[]>;
}

export default function Lovenood() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    answers: {}
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showMeetingPopup, setShowMeetingPopup] = useState(false);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const formContainerRef = useRef<HTMLDivElement>(null);

  const questions = [
    {
      id: 'brand_story',
      category: 'Brand & Vision',
      question: 'Kelly, what is Lovenood\'s unique brand story, and how would you like it reflected in your content?',
      type: 'textarea',
      placeholder: 'Share your brand\'s journey and vision...'
    },
    {
      id: 'content_goals',
      category: 'Content Goals',
      question: 'What are your primary content goals for Lovenood?',
      type: 'select',
      options: [
        'Building brand awareness and recognition',
        'Educating customers about intimate wellness',
        'Driving product sales and conversions',
        'Establishing thought leadership in the industry',
        'Creating an engaged community',
        'All of the above'
      ]
    },
    {
      id: 'target_audience',
      category: 'Target Audience',
      question: 'Who is Lovenood\'s ideal customer, and what content topics would resonate most with them?',
      type: 'textarea',
      placeholder: 'Describe your ideal customer profile and content preferences...'
    },
    {
      id: 'content_channels',
      category: 'Content Channels',
      question: 'Which platforms are most important for Lovenood\'s content strategy?',
      type: 'multiselect',
      options: [
        'Instagram',
        'TikTok',
        'YouTube',
        'Blog/Website',
        'Email Marketing',
        'Pinterest',
        'LinkedIn',
        'Facebook',
        'Twitter/X',
        'Other'
      ]
    },
    {
      id: 'content_tone',
      category: 'Content Tone & Style',
      question: 'How would you describe Lovenood\'s ideal content tone and style?',
      type: 'select',
      options: [
        'Educational and informative',
        'Playful and fun',
        'Sophisticated and elegant',
        'Bold and provocative',
        'Warm and empathetic',
        'Professional and authoritative',
        'A mix of educational and playful'
      ]
    },
    {
      id: 'content_challenges',
      category: 'Content Challenges',
      question: 'What are the biggest challenges Lovenood faces with content creation and distribution?',
      type: 'multiselect',
      options: [
        'Consistency in posting',
        'Creating engaging content',
        'Navigating platform restrictions for intimate topics',
        'Limited internal resources',
        'Measuring content effectiveness',
        'Balancing educational and promotional content',
        'Standing out in a crowded market',
        'Other'
      ]
    },
    {
      id: 'content_budget',
      category: 'Content Budget',
      question: 'What monthly budget has Lovenood allocated for content creation?',
      type: 'select',
      options: [
        '$1,000 - $3,000 per month',
        '$3,000 - $5,000 per month',
        '$5,000 - $10,000 per month',
        '$10,000 - $15,000 per month',
        '$15,000+ per month',
        'Not yet determined'
      ]
    },
    {
      id: 'content_frequency',
      category: 'Content Frequency',
      question: 'How often does Lovenood need new content, and do you have specific content types in mind?',
      type: 'textarea',
      placeholder: 'Describe your ideal content schedule and types...'
    },
    {
      id: 'success_metrics',
      category: 'Success Metrics',
      question: 'How will you measure the success of our content collaboration?',
      type: 'multiselect',
      options: [
        'Engagement metrics (likes, comments, shares)',
        'Website traffic',
        'Conversion rates',
        'Sales attributed to content',
        'Brand awareness and sentiment',
        'Email list growth',
        'Follower growth',
        'Other'
      ]
    }
  ];

  // Ensure form container is visible when changing steps
  React.useEffect(() => {
    if (currentStep > 0 && formContainerRef.current) {
      formContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentStep]);

  const handleInputChange = (questionId: string, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: value
      }
    }));
  };

  const handleBasicInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNoteChange = (questionId: string, note: string) => {
    setNotes(prev => ({
      ...prev,
      [questionId]: note
    }));
  };

  const handleNext = () => {
    if (currentStep < questions.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleMultiSelectChange = (questionId: string, option: string) => {
    setFormData(prev => {
      const currentSelections = Array.isArray(prev.answers[questionId]) 
        ? prev.answers[questionId] as string[] 
        : [];
      
      const updatedSelections = currentSelections.includes(option)
        ? currentSelections.filter(item => item !== option)
        : [...currentSelections, option];
      
      return {
        ...prev,
        answers: {
          ...prev.answers,
          [questionId]: updatedSelections
        }
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://hook.eu2.make.com/9ghiqyof7m8hucgrj3b2lqtl4klrnwa1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'lovenood',
          timestamp: new Date().toISOString(),
          name: formData.name,
          email: formData.email,
          company: formData.company,
          answers: formData.answers,
          notes: notes
        }),
      });
      
      if (response.ok) {
        setShowMeetingPopup(true);
      } else {
        console.error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleMeeting = () => {
    window.location.href = 'https://cal.com/dsmarketing.meeting.with.pascoal/loovenood-ds-marketing-1st-content-creation-talk';
  };

  const handleDeclineMeeting = () => {
    setShowMeetingPopup(false);
    setShowPopup(true);
    // Reset form after successful submission
    setFormData({
      name: '',
      email: '',
      company: '',
      answers: {}
    });
    setNotes({});
    setCurrentStep(0);
  };

  return (
    <div className="page-container pt-20 min-h-screen">
      <div className="content-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="relative">
              <Heart className="w-16 h-16 text-golden animate-pulse" />
              <Sparkles className="w-8 h-8 text-pink-400 absolute -top-2 -right-2 animate-pulse" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Love<span className="text-golden">nood</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Personalized for Kelly Entwistle, Head of Partnerships at Lovenood
          </p>
          <div className="mt-6 max-w-3xl mx-auto bg-black/30 p-6 rounded-xl border border-gray-800">
            <p className="text-gray-300">
              We're excited to learn more about Lovenood's content needs to create a tailored strategy that elevates your intimate wellness brand and connects with your audience in meaningful ways.
            </p>
          </div>
        </motion.div>

        <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-8 mb-12 max-w-4xl mx-auto" ref={formContainerRef}>
          <QuestionNavigator isActive={currentStep === 0}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="text-2xl font-bold mb-6 text-golden">Let's Get Started</h2>
              <p className="text-gray-300 mb-8">
                Please provide some basic information before we dive into the specific questions about Lovenood's content creation needs.
              </p>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Your Name <span className="text-golden">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleBasicInfoChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                    placeholder="Kelly Entwistle"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address <span className="text-golden">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleBasicInfoChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                    placeholder="kelly@lovenood.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                    Company Name <span className="text-golden">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleBasicInfoChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                    placeholder="Lovenood"
                  />
                </div>
                
                <div className="pt-4">
                  <button
                    onClick={handleNext}
                    disabled={!formData.name || !formData.email || !formData.company}
                    className="w-full px-6 py-4 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue to Questions
                  </button>
                </div>
              </div>
            </motion.div>
          </QuestionNavigator>

          {questions.map((question, index) => {
            const stepNumber = index + 1;
            return (
              <QuestionNavigator 
                key={question.id} 
                isActive={currentStep === stepNumber}
              >
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">
                      Question {stepNumber} of {questions.length}
                    </span>
                    <span className="text-sm font-medium text-golden">
                      {question.category}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold">
                    {question.question}
                  </h3>
                  
                  {question.type === 'textarea' && (
                    <textarea
                      value={formData.answers[question.id] as string || ''}
                      onChange={(e) => handleInputChange(question.id, e.target.value)}
                      placeholder={question.placeholder}
                      className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white min-h-[120px]"
                    />
                  )}
                  
                  {question.type === 'select' && (
                    <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2 pb-2">
                      {question.options.map((option) => (
                        <div 
                          key={option}
                          onClick={() => handleInputChange(question.id, option)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                            formData.answers[question.id] === option
                              ? 'border-golden bg-golden/10'
                              : 'border-gray-800 bg-black/30 hover:border-gray-600'
                          }`}
                        >
                          <div className="flex items-center">
                            <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                              formData.answers[question.id] === option
                                ? 'border-golden'
                                : 'border-gray-600'
                            }`}>
                              {formData.answers[question.id] === option && (
                                <div className="w-3 h-3 rounded-full bg-golden" />
                              )}
                            </div>
                            <span className="text-gray-300">{option}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {question.type === 'multiselect' && (
                    <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2 pb-2">
                      {question.options.map((option) => {
                        const isSelected = Array.isArray(formData.answers[question.id]) && 
                          (formData.answers[question.id] as string[]).includes(option);
                        
                        return (
                          <div 
                            key={option}
                            onClick={() => handleMultiSelectChange(question.id, option)}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                              isSelected
                                ? 'border-golden bg-golden/10'
                                : 'border-gray-800 bg-black/30 hover:border-gray-600'
                            }`}
                          >
                            <div className="flex items-center">
                              <div className={`w-5 h-5 rounded-md border-2 mr-3 flex items-center justify-center ${
                                isSelected
                                  ? 'border-golden'
                                  : 'border-gray-600'
                              }`}>
                                {isSelected && (
                                  <CheckCircle className="w-3 h-3 text-golden" />
                                )}
                              </div>
                              <span className="text-gray-300">{option}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Notes (for internal use only)
                    </label>
                    <div className="flex items-start space-x-2">
                      <MessageSquare className="w-5 h-5 text-gray-400 mt-3" />
                      <textarea
                        value={notes[question.id] || ''}
                        onChange={(e) => handleNoteChange(question.id, e.target.value)}
                        placeholder="Add notes about this response..."
                        className="flex-1 px-4 py-3 rounded-lg bg-black/30 border border-gray-700 focus:outline-none focus:border-gray-500 text-gray-400 text-sm min-h-[80px]"
                      />
                    </div>
                  </div>
                  
                  <div className="pt-4 flex justify-between">
                    <button
                      onClick={handlePrevious}
                      className="px-6 py-3 border-2 border-gray-700 text-gray-300 rounded-full font-medium hover:border-golden hover:text-golden transition-all duration-300"
                    >
                      Previous
                    </button>
                    
                    {stepNumber < questions.length ? (
                      <button
                        onClick={handleNext}
                        disabled={
                          (question.type === 'select' && !formData.answers[question.id]) ||
                          (question.type === 'multiselect' && 
                            (!Array.isArray(formData.answers[question.id]) || 
                            (formData.answers[question.id] as string[]).length === 0))
                        }
                        className="px-6 py-3 bg-golden text-white rounded-full font-medium hover:bg-golden-600 transition-all duration-300 disabled:opacity-70"
                      >
                        Next Question
                      </button>
                    ) : (
                      <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="px-6 py-3 bg-golden text-white rounded-full font-medium hover:bg-golden-600 transition-all duration-300 disabled:opacity-70"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Responses'}
                      </button>
                    )}
                  </div>
                </div>
              </QuestionNavigator>
            );
          })}

          <QuestionNavigator isActive={currentStep > questions.length}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="text-center py-8"
            >
              <CheckCircle className="w-16 h-16 text-golden mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-4">Thank You!</h2>
              <p className="text-gray-300 mb-6">
                Your responses have been submitted successfully. We'll be in touch soon with a tailored content strategy for Lovenood.
              </p>
            </motion.div>
          </QuestionNavigator>
        </MouseFollowEffect>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-gradient-to-r from-pink-900/30 to-golden/20 rounded-2xl p-8 border border-golden/20">
            <h2 className="text-2xl font-bold mb-4">About Lovenood</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-golden mb-2">Here's what we found on Lovenood</h3>
                <p className="text-gray-300">
                  A revolutionary brand dedicated to transforming the intimate wellness space with products that prioritize both pleasure and health, creating a more open conversation around intimacy.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-golden mb-2">Innovative Approach</h3>
                <p className="text-gray-300">
                  By combining cutting-edge product design with educational content, Lovenood is breaking taboos and helping customers embrace their intimate wellness journey.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-golden mb-2">Community Focus</h3>
                <p className="text-gray-300">
                  Lovenood builds a supportive community where customers feel empowered to explore and discuss intimate wellness in a judgment-free environment.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-golden mb-2">Quality & Safety</h3>
                <p className="text-gray-300">
                  Every Lovenood product is crafted with premium, body-safe materials and undergoes rigorous testing to ensure the highest standards of quality and safety.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Meeting Popup */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: showMeetingPopup ? 1 : 0 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 ${
          showMeetingPopup ? 'visible' : 'invisible'
        }`}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: showMeetingPopup ? 1 : 0.9, opacity: showMeetingPopup ? 1 : 0 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-black/90 rounded-2xl p-8 max-w-md w-full border border-golden/30"
        >
          <div className="text-center">
            <Calendar className="w-16 h-16 text-golden mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-4">
              Schedule a Content Strategy Call
            </h2>
            <p className="text-lg text-gray-300 mb-6">
              Would you like to schedule a call to discuss your content needs in more detail?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleScheduleMeeting}
                className="px-6 py-3 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300"
              >
                Yes, Schedule Now
              </button>
              <button
                onClick={handleDeclineMeeting}
                className="px-6 py-3 border border-gray-600 text-gray-300 rounded-full font-semibold hover:border-gray-400 hover:text-white transition-all duration-300"
              >
                Not Right Now
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
      
      <SubmissionPopup 
        isOpen={showPopup} 
        onClose={() => setShowPopup(false)} 
        message="Thank you for your time Kelly! We'll be in touch shortly with a tailored content strategy for Lovenood."
      />
    </div>
  );
}